import * as firebase from 'firebase/app';
import 'firebase/auth';
import cloneDeep from 'lodash.clonedeep';
import * as DatabaseService from '@/services/Database.service';
import * as AuthMutations from '@/store/mutations/Auth.mutations';
import * as UserActions from '@/store/actions/User.actions';
import * as UserMutations from '@/store/mutations/User.mutations';
import * as AuthActions from '@/store/actions/Auth.actions';
// import * as workspaceHelpers from '@/helpers/workspaceHelpers';
import { isObjectEmpty, documentWithId } from '@/helpers/dataHelpers';

const getInitialState = () => ({
  loggedInUserId: null,
  authUser: {},
  logInError: null,
  isLogInFormLoading: false,
  isSessionAuthLoading: false,
  isGoogleAuthLoading: false,
  isSignUpFormLoading: false,
  signUpError: null,
  isUserLoggingOut: false,
});

const state = getInitialState();

const getters = {};

const mutations = {
  [AuthMutations.SET_LOGGED_IN_USER](state, payload) {
    state.isLogInFormLoading = false;
    state.isSessionAuthLoading = false;
    state.isGoogleAuthLoading = false;
    state.isSignUpFormLoading = false;
    state.logInError = null;
    state.signUpError = null;
    state.loggedInUserId = payload.loggedInUserId;
    state.authUser = cloneDeep(payload.authUser.toJSON());
  },

  [AuthMutations.SET_LOG_IN_ERROR](state, payload) {
    state.isLogInFormLoading = false;
    state.isSessionAuthLoading = false;
    state.isGoogleAuthLoading = false;
    state.isSignUpFormLoading = false;
    state.signUpError = null;
    state.logInError = payload;
  },

  [AuthMutations.LOG_IN_FORM_REQUEST_DATA](state) {
    state.isLogInFormLoading = true;
  },

  [AuthMutations.SET_LOG_OUT_IN_PROGRESS](state) {
    state.isUserLoggingOut = true;
  },

  [AuthMutations.LOG_OUT](state) {
    Object.assign(state, getInitialState());
  },

  [AuthMutations.SESSION_AUTH_REQUEST_DATA](state) {
    state.isSessionAuthLoading = true;
  },

  [AuthMutations.GOOGLE_AUTH_REQUEST_DATA](state) {
    state.isGoogleAuthLoading = true;
  },

  [AuthMutations.SET_SIGN_UP_ERROR](state, payload) {
    state.isLogInFormLoading = false;
    state.isSessionAuthLoading = false;
    state.isGoogleAuthLoading = false;
    state.isSignUpFormLoading = false;
    state.logInError = null;
    state.signUpError = payload;
  },

  [AuthMutations.SIGN_UP_FORM_REQUEST_DATA](state) {
    state.isSignUpFormLoading = true;
  },
};

const actions = {
  async [AuthActions.LOG_IN_WITH_EMAIL_AND_PASSWORD](context, payload) {
    const { userEmail, userPassword } = payload;
    context.commit(AuthMutations.LOG_IN_FORM_REQUEST_DATA);
    return new Promise(async (resolve, reject) => {
      try {
        const { user: authUser } = await firebase.auth().signInWithEmailAndPassword(userEmail, userPassword);
        await context.dispatch(AuthActions.FETCH_LAST_LOGGED_IN_USER, { userId: authUser.uid });

        context.commit(
          AuthMutations.SET_LOGGED_IN_USER,
          { loggedInUserId: authUser.uid, authUser },
        );
        resolve();
      } catch (err) {
        context.commit(AuthMutations.SET_LOG_IN_ERROR, err);
        console.error(err); // eslint-disable-line no-console
        reject(err);
      }
    });
  },

  async [AuthActions.LOG_IN_WITH_GOOGLE_AUTH](context) {
    context.commit(AuthMutations.GOOGLE_AUTH_REQUEST_DATA);
    const provider = new firebase.auth.GoogleAuthProvider();

    try {
      firebase.auth().signInWithRedirect(provider);
    } catch (error) {
      context.commit(AuthMutations.SET_LOG_IN_ERROR, error);
      console.error(error); // eslint-disable-line no-console
    }
  },

  async [AuthActions.LOG_IN_WITH_EXISTING_SESSION_TOKEN](context) {
    context.commit(AuthMutations.SESSION_AUTH_REQUEST_DATA);

    return new Promise(async (resolve, reject) => {
      const authUser = firebase.auth().currentUser;
      if (!authUser) { // No active firebase session found in browser.
        context.commit(AuthMutations.LOG_OUT);
        reject();
      // Session exists and Vuex already has user data in store.
      } else if (!isObjectEmpty(context.getters.loggedInUser)) {
        resolve();
      } else { // Session exists but we still need to fetch user data to store in Vuex.
        try {
          await context.dispatch(AuthActions.FETCH_LAST_LOGGED_IN_USER, { userId: authUser.uid });
          context.commit(
            AuthMutations.SET_LOGGED_IN_USER,
            { loggedInUserId: authUser.uid, authUser },
          );
          resolve();
        } catch (error) {
          console.error(error); // eslint-disable-line no-console
          context.commit(AuthMutations.SET_LOG_IN_ERROR, error);
          reject();
        }
      }
    });
  },

  async [AuthActions.LOG_OUT_USER](context) {
    context.commit(AuthMutations.SESSION_AUTH_REQUEST_DATA);
    context.commit(AuthMutations.SET_LOG_OUT_IN_PROGRESS);
    return new Promise(async (resolve, reject) => {
      try {
        await firebase.auth().signOut();
        context.commit(AuthMutations.LOG_OUT);
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject(err);
      }
    });
  },

  async [AuthActions.CREATE_USER_WITH_EMAIL_AND_PASSWORD](context, payload) {
    const { userEmail, userPassword } = payload;
    return new Promise(async (resolve, reject) => {
      try {
        context.commit(AuthMutations.SIGN_UP_FORM_REQUEST_DATA);
        const { user: authUser } = await firebase.auth().createUserWithEmailAndPassword(userEmail, userPassword);
        await context.dispatch(AuthActions.FETCH_LAST_LOGGED_IN_USER, { userId: authUser.uid });
        // we have valid user, so let's start the session with current user.
        context.commit(
          AuthMutations.SET_LOGGED_IN_USER,
          { loggedInUserId: authUser.uid, authUser },
        );
        resolve();
      } catch (err) {
        context.commit(AuthMutations.SET_SIGN_UP_ERROR, err);
        console.error(err); // eslint-disable-line no-console
        reject(err);
      }
    });
  },

  async [AuthActions.FETCH_LAST_LOGGED_IN_USER](context, payload) {
    return new Promise(async (resolve, reject) => {
      try {
        const { userId } = payload;
        const lastLoggedInUser = await DatabaseService.getLastLoggedInUserByAuthId(userId);

        if (!lastLoggedInUser.empty) { // if account has at least 1 user profile in the DB.
          const shapedLastLoggedInUser = documentWithId(lastLoggedInUser.docs[0]);
          context.commit(UserMutations.RECEIVE_USERS, { users: { [shapedLastLoggedInUser.id]: shapedLastLoggedInUser } }); // eslint-disable-line max-len
          const { workspaceId } = shapedLastLoggedInUser;
          await context.dispatch(UserActions.UPDATE_USER_LOGIN_TIMESTAMP, { userId, workspaceId });
        }
        resolve();
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
        reject(err);
      }
    });
  },

  async [AuthActions.VERIFY_PASSWORD_RESET_CODE](context, payload) {
    const email = await firebase.auth().verifyPasswordResetCode(payload.actionCode);

    return email;
  },

  async [AuthActions.CONFIRM_PASSWORD_RESET](context, payload) {
    const { actionCode, newPassword } = payload;
    await firebase.auth().confirmPasswordReset(actionCode, newPassword);
  },

  async [AuthActions.SEND_PASSWORD_RESET_EMAIL](context, payload) {
    await firebase.auth().sendPasswordResetEmail(payload.userEmail);
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
